import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import {
  METER, TRADE,
} from 'src/enosikit/components/Chart/components/chartConstants';
import {
  DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PORTFOLIO,
  DATA_AGGREGATE_BY_PROPERTY, DATA_GROUP_BY_COUNTERPARTY,
  DATA_GROUP_BY_TRADE_TYPE,
} from 'src/util/constants';

import { getTooltipPosition, getTooltipWidth } from '../helpers/tooltip';
// Styles
const base = 2.5;

const TooltipContainer = styled.div`
width: max-content;
fontSize: ${base * 0.25}rem;
fontFamily: inherit;
color: #212529;
padding: ${base * 0.3}rem;
background-color: #fff;
border: ${base * 0.1}rem solid #f5f7fa;
border-radius: ${base * 0.1}rem;
`;

const MainHeader = styled.h5`
font-size: ${base * 0.4}rem;
margin-bottom: ${base * 0.1}rem;
`;

// End of child components rendering

/**
 * Description
 * @param {any} props
 * @returns {React.ReactElement} - ChartTooltip component
 */
function ChartTooltip({
  chartType, chartWidth,
  chartView, datum = undefined, tooltipUpdateFunc,
  tooltipProps = {
    tooltipFormat: null,
    unit: undefined,
  }, x = undefined,
}) {
  const {
    unit,
    unitLabel,
    tooltipDateFormat,
    tooltipData: staticTooltipData,
    getTooltipDataFunc,
    tooltipLabelFunc,
  } = tooltipProps;

  const tooltipRef = useRef(null);
  const lastTooltipTimestampRef = useRef(null);
  const datumTimestamp = datum?.fullDateObj;

  useEffect(() => {
    if (!datumTimestamp || lastTooltipTimestampRef.current === datumTimestamp.ts) {
      return;
    }

    lastTooltipTimestampRef.current = datumTimestamp.ts;
    tooltipUpdateFunc(datumTimestamp);
  }, [datumTimestamp, tooltipUpdateFunc]);

  if (!datum) {
    return null;
  }

  if (!datumTimestamp) {
    return null;
  }

  const tooltipData = getTooltipDataFunc
    ? getTooltipDataFunc(datumTimestamp) : staticTooltipData;

  const tooltipWidth = getTooltipWidth(tooltipRef);

  return (
    <g>
      <foreignObject x={getTooltipPosition(x, chartWidth, tooltipWidth)} width="1" height="100%" className="overflow-visible">
        <TooltipContainer ref={tooltipRef} className={`${chartType}-tooltip`}>
          <MainHeader>{datumTimestamp.toLocaleString(tooltipDateFormat)}</MainHeader>
          {
            tooltipLabelFunc(
              tooltipData,
              unitLabel,
              chartView,
              unit,
            )
          }
        </TooltipContainer>
      </foreignObject>
    </g>
  );
}

export default ChartTooltip;

ChartTooltip.propTypes = {
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PORTFOLIO,
      DATA_AGGREGATE_BY_PROPERTY,
    ]).isRequired,
  }).isRequired,
  chartType: PropTypes.oneOf([METER, TRADE]).isRequired,
  chartWidth: PropTypes.number.isRequired,
  datum: PropTypes.oneOfType([PropTypes.object]),
  tooltipProps: PropTypes.shape({
    isCarbon: PropTypes.bool.isRequired,
    unit: PropTypes.string,
    unitLabel: PropTypes.string.isRequired,
    tooltipDateFormat: PropTypes.oneOfType([PropTypes.object]).isRequired,
    tooltipData: PropTypes.oneOfType([PropTypes.object]),
    getTooltipDataFunc: PropTypes.func,
    tooltipFormat: PropTypes.string,
    tooltipLabelFunc: PropTypes.func.isRequired,
  }),
  tooltipUpdateFunc: PropTypes.func.isRequired,
  x: PropTypes.number,
};
